/**
 * Lượt mở app gọi `GET /v1/bootstrap` MỘT lần thay cho ~10 request lẻ (me, profile,
 * streak, checkin, inbox, quota, offer nạp nhanh, popup, hoa hồng, vận khí ngày).
 *
 * Cách dùng: bọc queryFn/hàm tải hiện có — `fromBootstrap("streak", getMyStreak)`.
 * Nơi gọi KHÔNG phải biết bootstrap tồn tại; mọi cache/react-query key giữ nguyên.
 *
 * Quy tắc:
 * - Bootstrap bắn lười ở lời gọi đầu tiên có token; các phần gọi cùng lúc chờ chung
 *   một promise → một request.
 * - Mỗi phần chỉ được lấy từ bootstrap MỘT lần. Lần sau (refetch, invalidate sau khi
 *   tiêu tiền…) đi thẳng endpoint lẻ — dữ liệu bootstrap là ảnh chụp lúc mở app.
 * - Quá `MAX_AGE_MS` thì bỏ ảnh chụp: phần nào tới muộn (vd vào trang chủ sau vài
 *   phút) mà dùng dữ liệu cũ là hiện số liệu sai.
 * - BE chưa có endpoint (404) → gọi endpoint lẻ.
 * - Phần nằm trong `failed`: hỏng LẺ TẺ → gọi endpoint lẻ cho riêng phần đó; hỏng
 *   từ một nửa trở lên (BE hết ngân sách thời gian = DB đang nghẽn) → ném
 *   `BootstrapOverloadError`, KHÔNG gọi lẻ. Phần đã có dữ liệu vẫn dùng bình thường.
 * - Lỗi khác (auth, 5xx, mạng) → NÉM đúng lỗi đó cho nơi gọi, như endpoint lẻ vẫn
 *   làm. KHÔNG tự lùi về 8 request lẻ: lúc server quá tải mà mỗi lượt mở app đẻ
 *   thêm cả chục request là đổ dầu vào lửa. Retry của react-query (lần sau) đi endpoint lẻ.
 */
import {
  getBoideToken,
  getAffiliateMe,
  getBootstrap,
  getDailyFortuneMe,
  getCheckinStatus,
  getInboxUnreadCount,
  getMyQuotaStatus,
  getMyStreak,
  listActiveAnnouncements,
  type BootstrapData,
  type BootstrapSection,
} from "@/lib/boide-api";

const MAX_AGE_MS = 15 * 1000;

/** Tỉ lệ phần hỏng (≥) để coi là server quá tải thay vì hỏng lẻ tẻ. */
const OVERLOAD_FAILED_RATIO = 0.5;

/**
 * Server quá tải — đừng gọi thêm. Là `Error` thường (không phải `TypeError`) nên
 * react-query KHÔNG thử lại (`query-retry.ts`), và `useBoideUser` giữ phiên.
 */
export class BootstrapOverloadError extends Error {
  constructor() {
    super("Máy chủ đang bận, vui lòng thử lại sau ít phút.");
    this.name = "BootstrapOverloadError";
  }
}

function isOverloaded(data: BootstrapData): boolean {
  const total = Object.keys(data).filter((key) => key !== "failed").length;
  return total > 0 && data.failed.length / total >= OVERLOAD_FAILED_RATIO;
}

type BootstrapState = {
  token: string;
  startedAt: number;
  promise: Promise<BootstrapData | null>;
  consumed: ReadonlySet<BootstrapSection>;
};

let state: BootstrapState | null = null;

function stateForToken(token: string): BootstrapState {
  if (state?.token === token) return state;
  // Token mới (vừa đăng nhập / đổi tài khoản) → một lượt bootstrap mới.
  const promise = getBootstrap();
  // Chặn "unhandled rejection" khi không phần nào chờ promise này nữa.
  promise.catch(() => undefined);
  state = { token, startedAt: Date.now(), promise, consumed: new Set() };
  return state;
}

/** Đánh dấu đã lấy (immutable — thay Set mới) và trả true nếu được phép lấy. */
function claim(current: BootstrapState, section: BootstrapSection): boolean {
  if (current.consumed.has(section)) return false;
  if (Date.now() - current.startedAt > MAX_AGE_MS) return false;
  state = { ...current, consumed: new Set([...current.consumed, section]) };
  return true;
}

/** Phần mà `null` là câu trả lời hợp lệ (chưa có offer), không phải dấu hiệu lỗi. */
const NULLABLE_SECTIONS: ReadonlySet<BootstrapSection> = new Set(["quick_topup_offer"]);

export async function fromBootstrap<T>(
  section: BootstrapSection,
  fallback: () => Promise<T>,
): Promise<T> {
  if (typeof window === "undefined") return fallback();
  const token = getBoideToken();
  if (!token) return fallback();

  const current = stateForToken(token);
  if (!claim(current, section)) return fallback();

  const data = await current.promise; // ném lỗi auth/5xx/mạng cho nơi gọi
  if (!data) return fallback();
  if (data.failed.includes(section)) {
    if (isOverloaded(data)) throw new BootstrapOverloadError();
    return fallback();
  }
  const value = data[section];
  if (value === null && !NULLABLE_SECTIONS.has(section)) return fallback();
  return value as T;
}

// ── queryFn dùng chung — đặt ở MỌI nơi khai báo cùng query key ──────────────
// Khai báo ở nhiều component (trang chủ, navbar, nút điểm danh…) nhưng mỗi phần chỉ
// lấy từ bootstrap một lần, nên bọc hết cũng không phát sinh request thừa.

export const loadMyStreak = () => fromBootstrap("streak", getMyStreak);
export const loadCheckinStatus = () => fromBootstrap("checkin", getCheckinStatus);
export const loadInboxUnreadCount = () => fromBootstrap("inbox_unread", getInboxUnreadCount);
export const loadMyQuotaStatus = () => fromBootstrap("quota", getMyQuotaStatus);
export const loadActiveAnnouncements = () =>
  fromBootstrap("announcements", listActiveAnnouncements);
export const loadAffiliateMe = () => fromBootstrap("affiliate", getAffiliateMe);
export const loadDailyFortuneMe = () => fromBootstrap("daily_fortune", getDailyFortuneMe);
